/**
 * The console's live connection.
 *
 * An operator who has to refresh to see a new conversation will miss one, so
 * this reconnects on its own and the views treat it as the source of "what
 * just happened" rather than polling.
 */

export type AgentEvent =
  | { t: "hello"; data: { operatorId: number; siteIds: number[] } }
  | { t: "conv.new"; data: { conversationId: number; message: unknown } }
  | { t: "msg.new"; data: unknown }
  /** A teammate (or this operator, elsewhere) corrected a message. Arrives bare from a conversation's own room, wrapped from the site's. */
  | { t: "msg.edit"; data: unknown }
  | { t: "msg.delete"; data: { conversationId?: number; messageId: number } }
  | { t: "conv.update"; data: Record<string, unknown> }
  | { t: "typing"; data: Record<string, unknown> }
  | { t: "msg.ack"; data: { clientMsgId: string; id: number; duplicate: boolean } }
  | { t: "error"; data: { message: string } }
  | { t: "agent.presence"; data: { operatorId: number; presence: "online" | "away" | "offline" } }
  /** A conversation was handed to this operator — by a teammate or by auto-assign. */
  | {
      t: "assigned";
      data: { conversationId: number; auto: boolean; by?: { id: number; displayName: string }; note?: string | null };
    }
  /** A teammate @mentioned this operator in an internal note. */
  | {
      t: "mention";
      data: { conversationId: number; messageId: number; from: { id: number; displayName: string }; excerpt: string };
    }
  /** What a visitor has typed but not sent yet. Never stored anywhere. */
  | { t: "draft"; data: { conversationId: number; text: string } }
  /**
   * Not from the server: this socket's own state. `connected: false` means
   * nothing is arriving right now; the `true` after it means the views should
   * re-read whatever they show, because events were missed in between.
   */
  | { t: "socket.status"; data: { connected: boolean } }
  | { t: "pong" };

type Listener = (event: AgentEvent) => void;

export class AgentSocket {
  private socket: WebSocket | null = null;
  private readonly listeners = new Set<Listener>();
  private attempt = 0;
  private timer: number | null = null;
  private stopped = false;
  private heartbeat: number | null = null;
  /** Re-announced on every reconnect, so a dropped socket does not reset "away" to "online". */
  private presence: "online" | "away" = "online";
  /** True from the moment a drop was announced until the socket is open again. */
  private down = false;
  /**
   * Conversations this console has opened. A subscribe sent before the socket
   * is open, or lost with a dropped one, would otherwise leave the open
   * conversation deaf to new messages, typing and drafts until the next click.
   */
  private readonly subscriptions = new Set<number>();

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: AgentEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  connect(): void {
    this.stopped = false;
    const url = `${location.origin.replace(/^http/, "ws")}/ws/agent`;
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      // Only coming *back* is news: on a clean first open every view has just
      // loaded its data and there is nothing to catch up on.
      if (this.down) {
        this.down = false;
        this.emit({ t: "socket.status", data: { connected: true } });
      }
      // A proxy that closes idle connections would otherwise silently drop a
      // console that is simply having a quiet afternoon.
      this.heartbeat = window.setInterval(() => this.send({ t: "ping" }), 25_000);
      this.send({ t: "presence.set", data: { presence: this.presence } });
      for (const conversationId of this.subscriptions) this.send({ t: "subscribe", data: { conversationId } });
    };

    socket.onmessage = (event) => {
      this.emit(JSON.parse(event.data as string) as AgentEvent);
    };

    socket.onclose = () => {
      if (this.heartbeat) window.clearInterval(this.heartbeat);
      // Said once per drop — however many reconnect attempts it takes — and
      // not for the close this console asked for.
      if (!this.stopped && !this.down) {
        this.down = true;
        this.emit({ t: "socket.status", data: { connected: false } });
      }
      if (!this.stopped) this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  private scheduleReconnect(): void {
    if (this.timer !== null) return;
    const base = Math.min(30_000, 1000 * 2 ** this.attempt);
    this.attempt += 1;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.connect();
    }, base / 2 + Math.random() * (base / 2));
  }

  send(frame: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(frame));
  }

  setPresence(presence: "online" | "away"): void {
    this.presence = presence;
    this.send({ t: "presence.set", data: { presence } });
  }

  subscribe(conversationId: number): void {
    if (this.subscriptions.has(conversationId)) return;
    this.subscriptions.add(conversationId);
    // Replayed on every reconnect, so keep it well inside the server's frame allowance.
    if (this.subscriptions.size > 20) this.subscriptions.delete(this.subscriptions.values().next().value!);
    this.send({ t: "subscribe", data: { conversationId } });
  }

  close(): void {
    this.stopped = true;
    this.down = false;
    if (this.timer !== null) window.clearTimeout(this.timer);
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    this.socket?.close();
  }
}

/**
 * A short two-tone chime, generated rather than fetched.
 *
 * A sound file would be another request, another asset to cache-bust and
 * another thing the CSP has to allow. Thirty lines of oscillator is cheaper
 * than all three.
 */
export function playNotification(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const gain = ctx.createGain();
    gain.gain.value = 0.08;
    gain.connect(ctx.destination);

    [880, 1170].forEach((frequency, index) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = frequency;
      osc.connect(gain);
      osc.start(ctx.currentTime + index * 0.12);
      osc.stop(ctx.currentTime + index * 0.12 + 0.11);
    });

    window.setTimeout(() => void ctx.close(), 600);
  } catch {
    // Autoplay policies, a missing AudioContext, a muted device: none of this
    // is worth an error in an operator's face.
  }
}
